import cx from "clsx";
import { range } from "common-utils/utils/array";
import ButtonSecondary from "kreate-common/modules/kreate-ui/components/ButtonSecondary";
import ButtonTransparent from "kreate-common/modules/kreate-ui/components/ButtonTransparent";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import React from "react";
import { MdCopyAll } from "react-icons/md";

import Decorator from "../../../../../PageHome/containers/SectionEventsFromRibbons/components/Decorator";

import styles from "./index.module.scss";

import { EM_DASH } from "@/modules/common-utils/unicode";
import AddressViewer from "@/modules/kreate-components/components/AddressViewer";
import { useToast } from "@/modules/kreate-contexts/ToastContext";

type AddressBech32 = string;
type AdaHandle = string;

type Props = {
  className?: string;
  style?: React.CSSProperties;
  address: AddressBech32;
  adaHandles: AdaHandle[] | null | undefined;
};

export default function DetailedAddressViewer({
  className,
  style,
  address,
  adaHandles,
}: Props) {
  const { showMessage } = useToast();

  const [showAllAdaHandles, setShowAllAdaHandles] = React.useState(false);
  const numShownAdaHandles = adaHandles?.length
    ? Math.min(adaHandles.length, showAllAdaHandles ? Infinity : 2)
    : 0;
  const numHiddenAdaHandles = adaHandles?.length
    ? adaHandles.length - numShownAdaHandles
    : 0;
  const shownAdaHandles = range(numShownAdaHandles).map((index) =>
    adaHandles?.[index] ? "$" + adaHandles[index] : EM_DASH
  );

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    showMessage({
      color: "success",
      title: "Copied to Clipboard",
    });
  };

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Row
        flexWrap="wrap"
        gap="8px"
        alignItems="baseline"
        justifyContent="center"
      >
        <Flex.Row gap="8px" alignItems="baseline" flex="0 0 auto">
          <AddressViewer
            className={styles.detailedAddress}
            address={address}
            prefixLength={8}
            suffixLength={8}
            unstyled
          />
          <Flex.Row alignSelf="center">
            <ButtonTransparent
              icon={<MdCopyAll className={styles.colorFgMain} size="24px" />}
              title="Copy address"
              onClick={async () => await copyToClipboard(address)}
            />
          </Flex.Row>
        </Flex.Row>
        {adaHandles?.length ? (
          <>
            <Flex.Row alignSelf="center" padding="0 8px">
              <Decorator.VerticalLine
                size="20px"
                variant="dashed"
                opacity="16%"
              />
            </Flex.Row>
            {shownAdaHandles.map((handle, index) => (
              <ButtonSecondary
                key={index}
                size="inherit"
                padding="inherit"
                style={{ padding: "4px 8px" }}
                title={`${handle} (click to copy to clipboard)`}
                onClick={() => copyToClipboard(handle)}
              >
                <span style={{ textTransform: "none" }}>{handle}</span>
              </ButtonSecondary>
            ))}
            {numHiddenAdaHandles ? (
              <ButtonSecondary
                size="inherit"
                padding="inherit"
                style={{ padding: "4px 8px" }}
                onClick={() => setShowAllAdaHandles(true)}
                title={`Show ${numHiddenAdaHandles} more ADA handles`}
              >
                {`+${numHiddenAdaHandles}`}
              </ButtonSecondary>
            ) : undefined}
          </>
        ) : null}
      </Flex.Row>
    </div>
  );
}
